/**
 * GPU Products Data Layer
 * Static catalog of GPU offerings for the products page
 */

import type { AirGPUInstanceType, AirGPURegion } from '@/lib/integrations/airgpu/types';
import { AIRGPU_INSTANCE_TYPES, AIRGPU_REGIONS, isValidRegion } from '@/lib/integrations/airgpu/types';
import { BASE_HOURLY_RATES, REGIONAL_MULTIPLIERS, getRegionalMultiplier } from '@/lib/integrations/airgpu/pricing';
import { formatCurrency, formatHourlyRate as formatHourlyRateCurrency, DEFAULT_CURRENCY } from '@/lib/utils/currency';

// ============================================
// GPU PRODUCT TYPES
// ============================================

export interface GPUProduct {
  id: string;
  name: string;
  slug: string;
  instanceType: AirGPUInstanceType | null; // null = not rentable (withdrawn)
  consumerEquivalent: string;
  /**
   * EUR per hour, before regional multipliers.
   * For every rentable product this is BASE_HOURLY_RATES[instanceType].onDemand
   * in lib/integrations/airgpu/pricing.ts -- the table the billing cron
   * actually deducts from the wallet -- read through `rentable()` below, never
   * typed in. The only hand-written value is a withdrawn product's last rate.
   */
  basePrice: number;
  vram: number; // GB
  vcpus: number;
  ram: number; // GB
  useCases: string[];
  available: boolean;
  /**
   * Previously sold, now pulled from sale. Shown as "Unavailable" rather than
   * "Coming Soon", and left out of the coming-soon spotlight and the compare
   * widget. Implies `available: false` and `instanceType: null`.
   */
  withdrawn?: boolean;
  badge?: 'Popular' | 'Best Value' | 'New' | 'Pro';
  tier: 'entry' | 'mid' | 'high' | 'premium';
  series: 'datacenter' | 'rtx';
}

type CatalogCopy = Omit<
  GPUProduct,
  'instanceType' | 'basePrice' | 'vram' | 'vcpus' | 'ram' | 'available' | 'withdrawn'
>;

/**
 * A product customers can rent. Price and specs come from the tables the
 * machine is billed and provisioned from, so the card cannot advertise a rate
 * the cron does not charge or a vCPU/RAM size the create call does not send
 * (the template once listed the T4 at €0.65 with 4 vCPU / 16 GB while billing
 * €0.94 for an 8 vCPU / 32 GB machine).
 */
function rentable(instanceType: AirGPUInstanceType, copy: CatalogCopy): GPUProduct {
  const spec = AIRGPU_INSTANCE_TYPES[instanceType];
  return {
    ...copy,
    instanceType,
    basePrice: BASE_HOURLY_RATES[instanceType].onDemand,
    vram: spec.vram,
    vcpus: spec.vcpus,
    ram: spec.ram,
    available: true,
  };
}

// ============================================
// GPU PRODUCT CATALOG
// ============================================

export const GPU_PRODUCTS: GPUProduct[] = [
  // ============================================
  // NVIDIA DATACENTER SERIES
  // Best availability around the globe, with Raytracing support
  // ============================================
  rentable('g4dn.xlarge', {
    id: 'tesla-t4-lite',
    name: 'NVIDIA Tesla T4 Lite',
    slug: 'tesla-t4-lite',
    consumerEquivalent: 'Similar to GeForce GTX 1080',
    useCases: ['Gaming', 'Streaming', 'Testing'],
    badge: 'New',
    tier: 'entry',
    series: 'datacenter',
  }),
  rentable('g4dn.2xlarge', {
    id: 'tesla-t4',
    name: 'NVIDIA Tesla T4',
    slug: 'tesla-t4',
    consumerEquivalent: 'Similar to GeForce GTX 1080',
    useCases: ['Gaming', 'Streaming', 'Light ML'],
    badge: 'Best Value',
    tier: 'entry',
    series: 'datacenter',
  }),
  rentable('g6.2xlarge', {
    id: 'nvidia-l4',
    name: 'NVIDIA L4',
    slug: 'nvidia-l4',
    consumerEquivalent: 'Similar to GeForce RTX 4060',
    useCases: ['Gaming', 'Content Creation', 'AI Inference'],
    badge: 'New',
    tier: 'mid',
    series: 'datacenter',
  }),
  rentable('g5.2xlarge', {
    id: 'nvidia-a10g',
    name: 'NVIDIA A10G',
    slug: 'nvidia-a10g',
    consumerEquivalent: 'Similar to GeForce RTX 3080',
    useCases: ['AAA Gaming', 'ML Training', 'Rendering'],
    badge: 'Popular',
    tier: 'high',
    series: 'datacenter',
  }),
  rentable('g6.4xlarge', {
    // The same L4 GPU as above with twice the vCPUs and RAM. Creatable from the
    // form all along but never listed, under the same name as the smaller size.
    id: 'nvidia-l4-pro',
    name: 'NVIDIA L4 Pro',
    slug: 'nvidia-l4-pro',
    consumerEquivalent: 'Similar to GeForce RTX 4060',
    useCases: ['Rendering', 'AI Inference', 'Heavy Multitasking'],
    tier: 'high',
    series: 'datacenter',
  }),
  {
    id: 'nvidia-l40s',
    name: 'NVIDIA L40S',
    slug: 'nvidia-l40s',
    // Withdrawn from sale (Sep 2026): the supplier reports capacity problems in
    // most locations and has pulled it from their own storefront. Its instance
    // types are gone from the rate table, so this is the one price in the
    // catalogue that is not read from BASE_HOURLY_RATES: the last rate it was
    // sold at, shown only beside "Unavailable" and never billed.
    instanceType: null,
    consumerEquivalent: 'Similar to GeForce RTX 4080',
    basePrice: 1.60,
    vram: 48,
    vcpus: 8,
    ram: 32,
    useCases: ['AAA Gaming', 'Professional Rendering', 'ML Training'],
    available: false,
    withdrawn: true,
    tier: 'premium',
    series: 'datacenter',
  },
];

// ============================================
// REGION DATA
// ============================================

export interface RegionOption {
  id: AirGPURegion;
  name: string;
  group: 'North America' | 'Europe' | 'Asia Pacific' | 'Africa';
}

/**
 * Mirrors the offered catalogue in `AIRGPU_REGIONS` (a deliberate subset of the
 * upstream API's regions), grouped for the storefront filters and the create
 * form's region picker. Keep in sync.
 */
export const REGION_OPTIONS: RegionOption[] = [
  // North America
  { id: 'us-east-1', name: AIRGPU_REGIONS['us-east-1'], group: 'North America' },
  { id: 'us-east-2', name: AIRGPU_REGIONS['us-east-2'], group: 'North America' },
  { id: 'us-west-1', name: AIRGPU_REGIONS['us-west-1'], group: 'North America' },
  { id: 'us-west-2', name: AIRGPU_REGIONS['us-west-2'], group: 'North America' },
  { id: 'ca-central-1', name: AIRGPU_REGIONS['ca-central-1'], group: 'North America' },
  // Europe
  { id: 'eu-west-1', name: AIRGPU_REGIONS['eu-west-1'], group: 'Europe' },
  { id: 'eu-west-2', name: AIRGPU_REGIONS['eu-west-2'], group: 'Europe' },
  { id: 'eu-central-1', name: AIRGPU_REGIONS['eu-central-1'], group: 'Europe' },
  { id: 'eu-north-1', name: AIRGPU_REGIONS['eu-north-1'], group: 'Europe' },
  // Asia Pacific
  { id: 'ap-northeast-1', name: AIRGPU_REGIONS['ap-northeast-1'], group: 'Asia Pacific' },
  { id: 'ap-northeast-2', name: AIRGPU_REGIONS['ap-northeast-2'], group: 'Asia Pacific' },
  { id: 'ap-southeast-1', name: AIRGPU_REGIONS['ap-southeast-1'], group: 'Asia Pacific' },
  { id: 'ap-southeast-2', name: AIRGPU_REGIONS['ap-southeast-2'], group: 'Asia Pacific' },
  { id: 'ap-south-1', name: AIRGPU_REGIONS['ap-south-1'], group: 'Asia Pacific' },
  { id: 'ap-east-1', name: AIRGPU_REGIONS['ap-east-1'], group: 'Asia Pacific' },
  // Africa
  { id: 'af-south-1', name: AIRGPU_REGIONS['af-south-1'], group: 'Africa' },
];

export const REGION_GROUPS = ['North America', 'Europe', 'Asia Pacific', 'Africa'] as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get GPU product by ID
 */
export function getGPUProduct(id: string): GPUProduct | undefined {
  return GPU_PRODUCTS.find((gpu) => gpu.id === id);
}

/**
 * Get GPU product by instance type
 */
export function getGPUProductByInstanceType(instanceType: AirGPUInstanceType): GPUProduct | undefined {
  return GPU_PRODUCTS.find((gpu) => gpu.instanceType === instanceType);
}

/**
 * Get available GPU products only
 */
export function getAvailableGPUs(): GPUProduct[] {
  return GPU_PRODUCTS.filter((gpu) => gpu.available);
}

/**
 * Get coming soon GPU products (not yet on sale; excludes withdrawn ones)
 */
export function getComingSoonGPUs(): GPUProduct[] {
  return GPU_PRODUCTS.filter((gpu) => !gpu.available && !gpu.withdrawn);
}

/**
 * Status label for a product that cannot be rented
 */
export function getUnavailableLabel(gpu: GPUProduct): string {
  return gpu.withdrawn ? 'Unavailable' : 'Coming Soon';
}

/**
 * Calculate regional price for a GPU
 */
export function getRegionalPrice(gpu: GPUProduct, region: AirGPURegion): number {
  const multiplier = getRegionalMultiplier(region);
  return Math.round(gpu.basePrice * multiplier * 100) / 100;
}

/**
 * Get the minimum and maximum price for a GPU across all regions
 */
export function getPriceRange(gpu: GPUProduct): { min: number; max: number } {
  const multipliers = Object.values(REGIONAL_MULTIPLIERS);
  const minMultiplier = Math.min(...multipliers);
  const maxMultiplier = Math.max(...multipliers);

  return {
    min: Math.round(gpu.basePrice * minMultiplier * 100) / 100,
    max: Math.round(gpu.basePrice * maxMultiplier * 100) / 100,
  };
}

/**
 * Format price for display
 */
export function formatPrice(amount: number): string {
  return formatCurrency(amount, DEFAULT_CURRENCY);
}

/**
 * Format hourly rate for display
 */
export function formatHourlyRate(amount: number): string {
  return formatHourlyRateCurrency(amount, DEFAULT_CURRENCY);
}

/**
 * Get region display name
 *
 * Accepts any string because it is also used for stored machine rows, which
 * may reference a region that is no longer offered; those fall back to the
 * raw region code rather than rendering nothing.
 */
export function getRegionName(region: string): string {
  return isValidRegion(region) ? AIRGPU_REGIONS[region] : region;
}

/**
 * Get regions grouped by geographic area
 */
export function getGroupedRegions(): Record<string, RegionOption[]> {
  const grouped: Record<string, RegionOption[]> = {};

  for (const group of REGION_GROUPS) {
    grouped[group] = REGION_OPTIONS.filter((r) => r.group === group);
  }

  return grouped;
}

// ============================================
// FILTER OPTIONS
// ============================================

export const GPU_TIER_OPTIONS = [
  { id: 'entry', name: 'Entry', description: 'Great for casual gaming and streaming' },
  { id: 'mid', name: 'Mid-Range', description: 'Good for most games and content creation' },
  { id: 'high', name: 'High-End', description: 'Excellent for AAA games and ML workloads' },
  { id: 'premium', name: 'Premium', description: 'Best performance for demanding tasks' },
] as const;

export const GPU_SERIES_OPTIONS = [
  { id: 'datacenter', name: 'Datacenter', description: 'NVIDIA datacenter GPUs with raytracing' },
  { id: 'rtx', name: 'RTX', description: 'NVIDIA RTX professional series' },
] as const;

const PRICE_STEP_CENTS = 5;
const rentableCents = GPU_PRODUCTS
  .filter((gpu) => gpu.available)
  .map((gpu) => Math.round(gpu.basePrice * 100));

/**
 * Bounds of the /products price slider: the cheapest and dearest rentable
 * base rate, widened to the slider step. Derived so a new or repriced GPU can
 * never fall outside the default filter and vanish from the unfiltered grid
 * (the old fixed 0.60-1.60 range would hide the L4 Pro). Computed in whole
 * cents so the bounds are exact two-decimal values that survive the
 * `toFixed(2)` round trip through the URL.
 */
export const PRICE_RANGE = {
  min: (Math.floor(Math.min(...rentableCents) / PRICE_STEP_CENTS) * PRICE_STEP_CENTS) / 100,
  max: (Math.ceil(Math.max(...rentableCents) / PRICE_STEP_CENTS) * PRICE_STEP_CENTS) / 100,
  step: PRICE_STEP_CENTS / 100,
} as const;
